import React, { useEffect, useState } from "react";
import { api } from "../api";
import { useAuth } from "../auth";
import { useDevices } from "../devices";
import DevicePicker from "../components/DevicePicker";
import { MapPinned, Clock, Route, ChevronRight, ArrowLeft, AlertTriangle } from "lucide-react";
import { Marker, Polyline } from "react-leaflet";
import LeafletMap, { startIcon, endIcon, FitBounds, useUserFallbackCenter } from "../components/LeafletMap";

function fmtDateIST(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-IN", {
    weekday: "short", day: "2-digit", month: "short",
    timeZone: "Asia/Kolkata",
  });
}
function fmtTimeIST(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleTimeString("en-IN", {
    hour: "2-digit", minute: "2-digit", hour12: true, timeZone: "Asia/Kolkata",
  });
}
function durationMin(a, b) {
  if (!a || !b) return "—";
  const m = Math.round((new Date(b) - new Date(a)) / 60000);
  if (m < 60) return `${m} min`;
  return `${Math.floor(m / 60)}h ${m % 60}m`;
}

function TripSkeleton() {
  return (
    <div className="px-4 pt-3 space-y-2.5">
      {[...Array(5)].map((_, i) => (
        <div key={i} className="bg-white border border-surface-border rounded-2xl p-4 animate-pulse">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-surface-muted shrink-0" />
            <div className="flex-1 space-y-2">
              <div className="h-4 bg-surface-muted rounded-lg w-2/5" />
              <div className="h-3 bg-surface-muted rounded-lg w-3/5" />
            </div>
            <div className="space-y-1.5 items-end flex flex-col">
              <div className="h-5 bg-surface-muted rounded-lg w-10" />
              <div className="h-3 bg-surface-muted rounded-lg w-5" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

export default function Trips() {
  const { t } = useAuth();
  const { selectedDeviceId } = useDevices();
  const deviceId = selectedDeviceId;
  const fallbackCenter = useUserFallbackCenter();
  const [trips, setTrips] = useState([]);
  const [selected, setSelected] = useState(null);
  const [routeTrip, setRouteTrip] = useState(null);
  const [routeError, setRouteError] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!deviceId) { setTrips([]); setLoading(false); return; }
    setLoading(true);
    (async () => {
      try {
        const { data } = await api.get(`/trips/${deviceId}`);
        setTrips(data);
      } finally { setLoading(false); }
    })();
  }, [deviceId]);

  const openTrip = async (tp) => {
    setSelected(tp);
    setRouteTrip(null);
    setRouteError(false);
    try {
      const { data } = await api.get(`/trips/id/${tp.id}/route`);
      setRouteTrip(data);
    } catch (_) {
      setRouteError(true);
    }
  };

  if (selected && !routeTrip && !routeError) {
    return (
      <div className="h-[100dvh] w-full flex flex-col items-center justify-center bg-surface-soft gap-5">
        <div className="w-12 h-12 rounded-2xl bg-gradient-to-tr from-brand-500 to-accent-cyan flex items-center justify-center shadow-brand">
          <div className="w-6 h-6 border-[3px] border-white border-t-transparent rounded-full animate-spin" />
        </div>
        <p className="text-[13px] font-semibold text-ink-soft">Loading route…</p>
        <button
          onClick={() => { setSelected(null); setRouteError(false); }}
          className="text-[12px] text-brand-600 font-semibold hover:underline"
        >
          Cancel
        </button>
      </div>
    );
  }

  if (selected && routeError) {
    return (
      <div className="h-[100dvh] w-full flex flex-col items-center justify-center bg-surface-soft gap-4 px-8 text-center">
        <div className="w-16 h-16 rounded-2xl bg-amber-50 border border-amber-100 flex items-center justify-center">
          <AlertTriangle size={28} className="text-alert" />
        </div>
        <div>
          <p className="text-[16px] font-heading font-bold text-ink">Could not load route</p>
          <p className="text-[13px] text-ink-soft mt-1.5 max-w-xs leading-relaxed">
            The trip route data is unavailable. This trip may still be active or have insufficient GPS points.
          </p>
        </div>
        <button
          onClick={() => { setSelected(null); setRouteError(false); }}
          className="mt-1 px-6 py-2.5 rounded-xl bg-brand-600 text-white text-[13px] font-bold shadow-brand hover:opacity-95 transition-opacity"
        >
          Back to Trips
        </button>
      </div>
    );
  }

  if (selected && routeTrip) {
    const pts = (routeTrip.points || []).map((p) => [p.lat, p.lon]);
    const mapCenter = pts[0] || fallbackCenter;
    return (
      <div data-testid="trip-route-screen" className="h-[100dvh] w-full relative overflow-hidden">
        <div className="absolute top-0 left-0 right-0 z-[450] px-4 pt-4">
          <button
            data-testid="trip-back"
            onClick={() => { setSelected(null); setRouteTrip(null); setRouteError(false); }}
            className="glass-card rounded-full px-3 py-2 flex items-center gap-1.5 text-[13px] font-semibold text-ink"
          >
            <ArrowLeft size={15} /> {t.back}
          </button>
          <div className="mt-2 glass-card rounded-2xl px-4 py-3.5">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-[14px] font-heading font-bold text-ink">{fmtDateIST(routeTrip.start_time)}</div>
                <div className="text-[12px] text-ink-soft mt-0.5">
                  {fmtTimeIST(routeTrip.start_time)} → {fmtTimeIST(routeTrip.end_time)}
                </div>
              </div>
              <div className="text-right">
                <div className="text-[20px] font-mono font-black text-brand-600 leading-none">{routeTrip.distance_km}</div>
                <div className="text-[10px] font-bold text-ink-muted uppercase tracking-wide mt-0.5">km · {durationMin(routeTrip.start_time, routeTrip.end_time)}</div>
              </div>
            </div>
            {pts.length === 0 && (
              <div className="mt-2.5 flex items-center gap-2 text-[12px] text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2 font-semibold">
                <AlertTriangle size={13} className="shrink-0" />
                No GPS points recorded for this trip yet
              </div>
            )}
          </div>
        </div>
        <div className="absolute inset-0">
          <LeafletMap center={mapCenter} zoom={pts.length > 0 ? 14 : 12} testid="trip-map">
            {pts.length > 0 && <Marker position={pts[0]} icon={startIcon} />}
            {pts.length > 1 && <Marker position={pts[pts.length - 1]} icon={endIcon} />}
            {pts.length > 1 && (
              <Polyline positions={pts} pathOptions={{ color: "#4f46e5", weight: 5, opacity: 0.9 }} />
            )}
            <FitBounds positions={pts} />
          </LeafletMap>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="trips-screen" className="min-h-[100dvh] bg-surface-soft pb-28">

      {/* Sticky frosted header */}
      <div className="screen-header px-5 pt-6 pb-4">
        <div className="flex items-center justify-between gap-2">
          <div>
            <h1 className="text-[22px] font-heading font-black text-ink">{t.tripHistory}</h1>
            <p className="text-[12px] text-ink-muted mt-0.5 font-body">
              {trips.length} {t.trips.toLowerCase()}
            </p>
          </div>
          <DevicePicker />
        </div>
      </div>

      {loading ? (
        <TripSkeleton />
      ) : trips.length === 0 ? (
        <div data-testid="trips-empty" className="flex flex-col items-center justify-center py-20 text-center px-6">
          <div className="w-20 h-20 rounded-2xl bg-gradient-to-tr from-brand-500/10 to-accent-cyan/10 flex items-center justify-center border border-brand-500/10 mb-5">
            <Route size={32} className="text-brand-400" />
          </div>
          <h3 className="text-[16px] font-heading font-bold text-ink">{t.noTrips}</h3>
          <p className="text-[13px] text-ink-soft mt-2 max-w-[240px] leading-relaxed">
            Start your car to begin recording your first trip automatically.
          </p>
        </div>
      ) : (
        <ul data-testid="trips-list" className="px-4 pt-3 space-y-2.5">
          {trips.map((tp) => (
            <li key={tp.id}>
              <button
                data-testid={`trip-item-${tp.id}`}
                onClick={() => openTrip(tp)}
                className="w-full bg-white border border-surface-border rounded-2xl p-4 hover:border-brand-200/70 hover:shadow-card-md transition-all duration-200 text-left group card-hover-lift"
              >
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-tr from-brand-500/10 to-accent-cyan/10 text-brand-600 flex items-center justify-center border border-brand-500/10 shrink-0 transition-all duration-300 group-hover:from-brand-500 group-hover:to-accent-cyan group-hover:text-white group-hover:shadow-brand group-hover:border-transparent">
                    <MapPinned size={20} strokeWidth={2} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-heading font-bold text-[14px] text-ink">{fmtDateIST(tp.start_time)}</span>
                      {tp.status === "active" && (
                        <span className="text-[9px] font-bold uppercase tracking-wider bg-moving/10 text-moving px-2 py-0.5 rounded-full border border-moving/20">
                          {t.active}
                        </span>
                      )}
                    </div>
                    <div className="text-[12px] text-ink-muted flex items-center gap-2 mt-0.5 flex-wrap">
                      <span className="inline-flex items-center gap-1">
                        <Clock size={11} />
                        {fmtTimeIST(tp.start_time)} — {fmtTimeIST(tp.end_time)}
                      </span>
                      <span className="text-ink-subtle">·</span>
                      <span>{durationMin(tp.start_time, tp.end_time)}</span>
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-[18px] font-mono font-black text-brand-600 leading-none">{tp.distance_km}</div>
                    <div className="text-[10px] font-bold text-ink-muted uppercase tracking-wide mt-0.5">km</div>
                  </div>
                  <ChevronRight size={15} className="text-ink-subtle group-hover:text-brand-400 transition-colors ml-1 shrink-0" />
                </div>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
